import { HTMLAttributes, forwardRef, ThHTMLAttributes, TdHTMLAttributes, createContext, useContext } from 'react';
import { cn } from '@/lib/utils';

export type TableDensity = 'comfortable' | 'compact';

interface TableContextValue {
  density: TableDensity;
}

const TableContext = createContext<TableContextValue>({ density: 'comfortable' });

interface TableProps extends HTMLAttributes<HTMLTableElement> {
  density?: TableDensity;
}

const Table = forwardRef<HTMLTableElement, TableProps>(
  ({ className, density = 'comfortable', ...props }, ref) => (
    <TableContext.Provider value={{ density }}>
      <div className="w-full overflow-auto">
        <table ref={ref} className={cn('w-full caption-bottom text-sm', className)} {...props} />
      </div>
    </TableContext.Provider>
  )
);
Table.displayName = 'Table';

const TableHeader = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <thead ref={ref} className={cn('bg-gray-50 dark:bg-gray-800', className)} {...props} />
  )
);
TableHeader.displayName = 'TableHeader';

const TableBody = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <tbody ref={ref} className={cn('bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700', className)} {...props} />
  )
);
TableBody.displayName = 'TableBody';

const TableRow = forwardRef<HTMLTableRowElement, HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn('transition-colors hover:bg-gray-50 dark:hover:bg-gray-700', className)}
      {...props}
    />
  )
);
TableRow.displayName = 'TableRow';

// Density-aware cell padding. Compact rows are denser on desktop and keep
// a 44px-tall tap target on mobile via the larger py value at small widths.
const headPaddingByDensity: Record<TableDensity, string> = {
  comfortable: 'px-6 py-3',
  compact: 'px-4 py-2',
};

const cellPaddingByDensity: Record<TableDensity, string> = {
  comfortable: 'px-6 py-4',
  compact: 'px-4 py-2 max-md:py-3',
};

const TableHead = forwardRef<HTMLTableCellElement, ThHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => {
    const { density } = useContext(TableContext);
    return (
      <th
        ref={ref}
        className={cn(
          headPaddingByDensity[density],
          'text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider',
          className
        )}
        {...props}
      />
    );
  }
);
TableHead.displayName = 'TableHead';

const TableCell = forwardRef<HTMLTableCellElement, TdHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => {
    const { density } = useContext(TableContext);
    return (
      <td
        ref={ref}
        className={cn(
          cellPaddingByDensity[density],
          'text-sm text-gray-900 dark:text-gray-100',
          className
        )}
        {...props}
      />
    );
  }
);
TableCell.displayName = 'TableCell';

export { Table, TableHeader, TableBody, TableRow, TableHead, TableCell };
